import { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";

// Shared Components
import Navigation from "./components/shared/Navigation";
import HeroSection from "./components/shared/HeroSection";
import FAQSection from "./components/shared/FAQSection";
import TestimonialSection from "./components/shared/TestimonialSection";
import TeamSection from "./components/shared/TeamSection";
import PartnersSection from "./components/shared/PartnersSection";
import ContactSection from "./components/shared/ContactSection";
import Footer from "./components/shared/Footer";

// Section Content
import IndividualContent from "./components/sections/IndividualContent";
import CorporateContent from "./components/sections/CorporateContent";
import PartnerContent from "./components/sections/PartnerContent";

// Team Page
import TeamPage from "./pages/TeamPage";

// Styles
import "./styles/design-system.css";
import "./App.css";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PrivacyPolicyArchive from "./pages/PrivacyPolicyArchive";
import PrivacyPolicyArchiveDetail from "./pages/PrivacyPolicyArchiveDetail";
import NotFoundPage from "./pages/NotFound";
import BlogsPage from "./pages/blogs/BlogsPage";
import BlogDetail from "./pages/blogs/BlogDetail";
import DeleteAccount from "./pages/DeleteAccount";

// Lab Booking flow
import { LabBookingProvider } from "./features/labBooking/context/LabBookingContext";

// Analytics (GA4 via Firebase)
import { initAnalytics, initButtonClickTracking, trackPageView } from "./lib/analytics";
import CompareLabsPage from "./features/labBooking/pages/CompareLabsPage";
import CartPage from "./features/labBooking/pages/CartPage";
import LoginPage from "./features/labBooking/pages/LoginPage";
import PatientDetailsPage from "./features/labBooking/pages/PatientDetailsPage";
import ReviewOrderPage from "./features/labBooking/pages/ReviewOrderPage";
import OrderConfirmedPage from "./features/labBooking/pages/OrderConfirmedPage";

function HomePage() {
  const [activeTab, setActiveTab] = useState("individual");

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sectionId = params.get("section");

    if (sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  // Scroll to section handler
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handle contact click - scroll to contact section
  const handleContactClick = () => {
    scrollToSection("contact");
  };

  return (
    <div className="App" style={{ background: "#FFFFFF" }}>
      {/* Navigation */}
      <Navigation
        onSectionClick={scrollToSection}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {/* Hero Section with Tab Switcher */}
      <HeroSection
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onContactClick={handleContactClick}
      />
      {/* Dynamic Content Based on Active Tab */}
      <AnimatePresence mode="wait">
        {activeTab === "individual" && (
          <IndividualContent
            key="individual"
            onContactClick={handleContactClick}
          />
        )}
        {activeTab === "corporate" && (
          <CorporateContent
            key="corporate"
            onContactClick={handleContactClick}
          />
        )}
        {activeTab === "partner" && (
          <PartnerContent key="partner" onContactClick={handleContactClick} />
        )}
      </AnimatePresence>
      {/* Shared Sections Ordered as Requested */}
      <TestimonialSection variant={activeTab} /> {/* 1. Testimonials Above */}
      <FAQSection variant={activeTab} /> {/* 2. Common Questions Middle */}
      <TeamSection /> {/* 3. Leadership Below */}
      <PartnersSection />
      <ContactSection />
      <Footer onSectionClick={scrollToSection} setActiveTab={setActiveTab} />
    </div>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null;
}

function App() {
  useEffect(() => {
    initAnalytics().then(() => initButtonClickTracking());
  }, []);

  return (
    <BrowserRouter>
      <AnalyticsRouteTracker />
      <LabBookingProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blogs/:id" element={<BlogDetail />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route
            path="/privacy"
            element={<Navigate to="/privacy-policy" replace />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/deleteAccount" element={<DeleteAccount />} />
          <Route
            path="/privacy-policy/archive"
            element={<PrivacyPolicyArchive />}
          />
          <Route
            path="/privacy-policy/archive/:slug"
            element={<PrivacyPolicyArchiveDetail />}
          />
          <Route path="/lab-booking/compare" element={<CompareLabsPage />} />
          <Route path="/lab-booking/cart" element={<CartPage />} />
          <Route path="/lab-booking/login" element={<LoginPage />} />
          <Route
            path="/lab-booking/patient-details"
            element={<PatientDetailsPage />}
          />
          <Route path="/lab-booking/review" element={<ReviewOrderPage />} />
          <Route
            path="/lab-booking/confirmed"
            element={<OrderConfirmedPage />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </LabBookingProvider>
    </BrowserRouter>
  );
}

export default App;
